import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { opApi, apiError } from "@/lib/api";
import { MorrLink } from "@/components/mc/MorrLink";

function ProblemRow({ p, onDone }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await opApi.post(`/tenant/email-problems/${p.id}/${path}`, body);
      if (path === "fix") toast.success(data.resent ? `Address saved and email re-sent to ${email}` : `Address saved${data.resent === false ? ", but the re-send failed — see Sent history" : ""}`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Something went wrong"); setBusy(false); }
  };
  const complaint = p.kind === "complaint";
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-t border-amber-200 first:border-t-0 text-sm" data-testid={`email-problem-${p.id}`}>
      <div className="flex-1 min-w-[220px]">
        <div className="font-medium">{p.recipient_name || p.to_address} <span className="text-xs font-normal text-amber-800">· {complaint ? "marked your email as spam" : "email bounced"}</span></div>
        <div className="text-xs text-amber-900/80 truncate">{p.to_address} · “{p.subject}”{p.detail ? ` · ${p.detail}` : ""}</div>
      </div>
      {complaint ? (
        <span className="text-xs text-amber-900/80 max-w-xs">Don't re-send. They won't get marketing emails from you.</span>
      ) : (
        <>
          <Input className="rounded-none h-8 w-56 bg-white" type="email" placeholder="Correct email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`email-problem-input-${p.id}`} />
          <Button size="sm" className="rounded-none h-8" disabled={busy || !email.includes("@")} onClick={() => act("fix", { email })} data-testid={`email-problem-fix-${p.id}`}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}{p.can_resend ? "Save & re-send" : "Save address"}
          </Button>
        </>
      )}
      <Button size="sm" variant="ghost" className="rounded-none h-8" disabled={busy} onClick={() => act("dismiss")} data-testid={`email-problem-dismiss-${p.id}`}>Dismiss</Button>
    </div>
  );
}

// Dashboard warning: customer emails Morr reported as bounced or marked as spam.
export function EmailProblems() {
  const [items, setItems] = useState([]);
  const load = () => opApi.get("/tenant/email-problems").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items.length) return null;
  return (
    <div className="border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 mb-6" data-testid="email-problems-banner">
      <div className="flex flex-wrap items-center gap-2 font-semibold text-sm mb-1"><AlertTriangle className="h-4 w-4" /> {items.length} customer email{items.length > 1 ? "s" : ""} didn't get through
        <MorrLink to="emails" label="View email log in Morr Access" testId="email-problems-morr-link" className="ml-auto" />
      </div>
      {items.map((p) => <ProblemRow key={p.id} p={p} onDone={load} />)}
    </div>
  );
}
